/**
 * Main storage service for chart data management
 * Implements CRUD operations with IndexedDB backend
 */

import {
  ChartMetadata,
  SavedChart,
  StorageConfig,
  StorageResult,
  StorageErrorCode,
  ListChartsOptions
} from '../models/storage';
import { devLog, devWarn, devError } from '../utils/devLog';
import { ParsedChartData } from '../models/types';
import { IndexedDBWrapper, DBConfig } from './IndexedDBWrapper';
import { globalEventStream } from '../components/common/AstrologyChart/universal_architecture/core/EventStream';
import { createEvent } from '../components/common/AstrologyChart/universal_architecture/core/EventStream';
import { BaseEvent } from '../components/common/AstrologyChart/universal_architecture/types/EventTypes';
import { emitChartAccessed, emitChartSaved } from '../hooks/useRecentFiles';
import { ChartFieldBackups, ChartBackupMetadata } from '../types/ChartOverrides';
import { generateChartIdFromData } from '../utils/chartIdGenerator';
import { CURRENT_LIVE_INDEX } from '../types/MultipleCharts';

// Simple event system for storage readiness
type StorageEventType = 'ready' | 'error';
type StorageEventListener = (event: { type: StorageEventType; data?: any }) => void;

class StorageEventEmitter {
  private listeners: Map<StorageEventType, StorageEventListener[]> = new Map();
  
  on(event: StorageEventType, listener: StorageEventListener): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event)!.push(listener);
    
    // Return unsubscribe function
    return () => {
      const eventListeners = this.listeners.get(event);
      if (eventListeners) {
        const index = eventListeners.indexOf(listener);
        if (index > -1) {
          eventListeners.splice(index, 1);
        }
      }
    };
  }
  
  emit(event: StorageEventType, data?: any): void {
    const eventListeners = this.listeners.get(event);
    if (eventListeners) {
      eventListeners.forEach(listener => listener({ type: event, data }));
    }
  }
}

// Default configuration
const DEFAULT_CONFIG: StorageConfig = {
  databaseName: 'HoraCharts',
  version: 4, // Updated to include backups store
  maxRecentFiles: 10,
  compressionEnabled: false, // Can be enabled later
  autoSave: true
};

// Database schema
const DB_CONFIG: DBConfig = {
  name: DEFAULT_CONFIG.databaseName,
  version: DEFAULT_CONFIG.version,
  stores: [
    {
      name: 'charts',
      keyPath: 'id',
      indexes: [
        { name: 'name', keyPath: 'metadata.name', unique: false },
        { name: 'created', keyPath: 'metadata.created', unique: false },
        { name: 'modified', keyPath: 'metadata.modified', unique: false },
        { name: 'accessed', keyPath: 'metadata.accessed', unique: false }
      ]
    },
    {
      name: 'metadata',
      keyPath: 'id',
      indexes: [
        { name: 'name', keyPath: 'name', unique: true },
        { name: 'created', keyPath: 'created', unique: false },
        { name: 'modified', keyPath: 'modified', unique: false },
        { name: 'accessed', keyPath: 'accessed', unique: false }
      ]
    },
    {
      name: 'workspaces',
      keyPath: 'id',
      indexes: [
        { name: 'name', keyPath: 'name', unique: false },
        { name: 'created', keyPath: 'created', unique: false },
        { name: 'accessed', keyPath: 'accessed', unique: false },
        { name: 'isDefault', keyPath: 'isDefault', unique: false }
      ]
    },
    {
      name: 'backups',
      keyPath: 'chartId',
      indexes: [
        { name: 'updatedAt', keyPath: 'updatedAt', unique: false }
      ]
    }
  ]
};

/**
 * Storage service class
 */
export class StorageService {
  private db: IndexedDBWrapper;
  private config: StorageConfig;
  private initialized: boolean = false;
  private eventEmitter: StorageEventEmitter = new StorageEventEmitter();

  constructor(config: Partial<StorageConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.db = new IndexedDBWrapper(DB_CONFIG);
  }

  /**
   * Initialize the storage service
   */
  async initialize(): Promise<StorageResult<void>> {
    try {
      await this.db.initialize();
      this.initialized = true;
      
      // Emit ready event
      this.eventEmitter.emit('ready');
      
      // Also emit to global event stream
      globalEventStream.emit(createEvent<BaseEvent>(
        'storage.ready',
        'StorageService',
        { metadata: { timestamp: Date.now() } }
      ));
      
      return { success: true };
    } catch (error) {
      devError('Failed to initialize StorageService:', error);
      
      // Emit error event
      this.eventEmitter.emit('error', error);
      
      return {
        success: false,
        error: `Storage initialization failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
        errorCode: StorageErrorCode.STORAGE_UNAVAILABLE
      };
    }
  }

  /**
   * Check if service is initialized
   */
  isInitialized(): boolean {
    return this.initialized && this.db.isInitialized();
  }

  /**
   * DRY helper: Returns error result if not initialized, null otherwise
   */
  private checkInitialized<T>(): StorageResult<T> | null {
    if (!this.isInitialized()) {
      return {
        success: false,
        error: 'Storage service not initialized',
        errorCode: StorageErrorCode.STORAGE_UNAVAILABLE
      };
    }
    return null;
  }

  /**
   * Subscribe to storage events
   */
  onReady(listener: () => void): () => void {
    return this.eventEmitter.on('ready', listener);
  }

  /**
   * Subscribe to storage error events
   */
  onError(listener: (error: any) => void): () => void {
    return this.eventEmitter.on('error', ({ data }) => listener(data));
  }

  /**
   * Generate unique ID for chart
   */
  private generateId(): string {
    return `chart_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Validate chart name for uniqueness
   */
  async validateName(name: string, excludeId?: string): Promise<boolean> {
    try {
      const existingMetadata = await this.db.getByIndex('metadata', 'name', name);
      
      if (excludeId) {
        // Exclude the chart being updated
        return !existingMetadata.some(meta => meta.id !== excludeId);
      }
      
      return existingMetadata.length === 0;
    } catch (error) {
      return false;
    }
  }

  /**
   * Generate unique name based on base name
   */
  async generateUniqueName(baseName: string): Promise<string> {
    let name = baseName;
    let counter = 1;

    while (!(await this.validateName(name))) {
      name = `${baseName} (${counter})`;
      counter++;
    }

    return name;
  }

  /**
   * Calculate storage size for data
   */
  private calculateSize(data: any): number {
    try {
      return JSON.stringify(data).length;
    } catch {
      return 0;
    }
  }

  /**
   * Save chart field backups
   */
  async saveChartBackups(chartId: string, backups: ChartFieldBackups): Promise<StorageResult<void>> {
    try {
      if (!this.initialized) {
        return {
          success: false,
          error: 'Storage service not initialized'
        };
      }
      
      const metadata: ChartBackupMetadata = {
        chartId,
        backups,
        createdAt: Date.now(),
        updatedAt: Date.now()
      };
      
      // Check if backups store exists (for backward compatibility)
      try {
        await this.db.put('backups', metadata);
        return { 
          success: true
        };
      } catch (error) {
        // Store doesn't exist yet - silently ignore for now
        devWarn('Backups store not available yet, skipping save:', error);
        return { 
          success: true  // Return success to not break the flow
        };
      }
    } catch (error) {
      devError('Failed to save chart overrides:', error);
      return { 
        success: false, 
        error: `Failed to save backups: ${error}` 
      };
    }
  }

  /**
   * Get chart field backups
   */
  async getChartBackups(chartId: string): Promise<StorageResult<ChartBackupMetadata | null>> {
    try {
      if (!this.initialized) {
        return {
          success: false,
          error: 'Storage service not initialized'
        };
      }
      
      // Check if backups store exists (for backward compatibility)
      try {
        const backupsData = await this.db.get('backups', chartId);
        return { 
          success: true, 
          data: backupsData || null 
        };
      } catch (error) {
        // Store doesn't exist yet - return empty backups
        devWarn('Backups store not available yet:', error);
        return { 
          success: true, 
          data: null 
        };
      }
    } catch (error) {
      devError('Failed to get chart backups:', error);
      return { 
        success: false, 
        error: `Failed to get backups: ${error}` 
      };
    }
  }

  /**
   * Delete chart field backups
   */
  async deleteChartBackups(chartId: string): Promise<StorageResult<void>> {
    try {
      if (!this.initialized) {
        return {
          success: false,
          error: 'Storage service not initialized'
        };
      }
      
      await this.db.delete('backups', chartId);
      
      return { 
        success: true
      };
    } catch (error) {
      devError('Failed to delete chart overrides:', error);
      return { 
        success: false, 
        error: `Failed to delete backups: ${error}` 
      };
    }
  }

  /**
   * Save chart data
   */
  async saveChart(name: string, chartData: ParsedChartData): Promise<StorageResult<string>> {
    const notInit = this.checkInitialized<string>();
    if (notInit) return notInit;

    try {
      // Use chart_name from API response if available, otherwise fallback to provided name
      const chartName = chartData.apiResponse?.basic_info?.chart_name || name;
      const uniqueName = await this.generateUniqueName(chartName);
      
      const now = new Date();
      const id = this.generateId();

      // Generate content hash for duplicate detection (v4.1+)
      const hashResult = generateChartIdFromData(chartData);
      const contentHash = hashResult?.hash;

      const metadata: ChartMetadata = {
        id,
        name: uniqueName,
        created: now,
        modified: now,
        accessed: now,
        size: this.calculateSize(chartData),
        version: 1,
        chartDateTime: chartData.chartDateTime,
        contentHash
      };

      const savedChart: SavedChart = {
        metadata,
        chartData
      };

      // Debug: Log hora information being saved

      // Save both full chart and metadata separately for faster queries
      await this.db.put('charts', { id, ...savedChart });
      await this.db.put('metadata', metadata);


      // Emit chart saved event
      emitChartSaved(id, uniqueName);

      return {
        success: true,
        data: id
      };

    } catch (error) {
      devError('Failed to save chart:', error);
      
      if (error instanceof Error && error.message.includes('QuotaExceededError')) {
        return {
          success: false,
          error: 'Storage quota exceeded. Please delete some charts to free up space.',
          errorCode: StorageErrorCode.QUOTA_EXCEEDED
        };
      }
      
      return {
        success: false,
        error: `Failed to save chart: ${error instanceof Error ? error.message : 'Unknown error'}`,
        errorCode: StorageErrorCode.TRANSACTION_FAILED
      };
    }
  }

  async loadChart(id: string): Promise<StorageResult<SavedChart>> {
    const notInit = this.checkInitialized<SavedChart>();
    if (notInit) return notInit;

    try {
      const result = await this.db.get('charts', id);
      
      if (!result) {
        return {
          success: false,
          error: 'Chart not found',
          errorCode: StorageErrorCode.NOT_FOUND
        };
      }

      // Update access time
      const metadata = { ...result.metadata, accessed: new Date() };
      await this.db.put('metadata', metadata);

      const savedChart: SavedChart = {
        metadata,
        chartData: result.chartData,
        history: result.history || [],  // Include saved history
        currentHistoryIndex: result.currentHistoryIndex ?? CURRENT_LIVE_INDEX  // Include active index
      };

      // Debug: Log hora information being loaded


      // Emit chart accessed event
      emitChartAccessed(id, metadata.name);

      return {
        success: true,
        data: savedChart
      };

    } catch (error) {
      devError('Failed to load chart:', error);
      return {
        success: false,
        error: `Failed to load chart: ${error instanceof Error ? error.message : 'Unknown error'}`,
        errorCode: StorageErrorCode.TRANSACTION_FAILED
      };
    }
  }

  /**
   * Update chart history (snapshots) in storage
   * Called when history changes (new snapshot added, navigation, etc.)
   */
  async updateChartHistory(
    id: string,
    history: import('../types/MultipleCharts').ChartStateSnapshot[],
    currentHistoryIndex?: number
  ): Promise<StorageResult<void>> {
    const notInit = this.checkInitialized<void>();
    if (notInit) return notInit;

    try {
      const result = await this.db.get('charts', id);

      if (!result) {
        return {
          success: false,
          error: 'Chart not found',
          errorCode: StorageErrorCode.NOT_FOUND
        };
      }

      // Update chart with new history and currentHistoryIndex
      const updatedChart = {
        ...result,
        history,
        currentHistoryIndex: currentHistoryIndex ?? result.currentHistoryIndex ?? CURRENT_LIVE_INDEX,
        metadata: {
          ...result.metadata,
          modified: new Date()
        }
      };

      await this.db.put('charts', { id, ...updatedChart });

      // Also update metadata modified time
      await this.db.put('metadata', updatedChart.metadata);

      return { success: true };

    } catch (error) {
      devError('Failed to update chart history:', error);
      return {
        success: false,
        error: `Failed to update chart history: ${error instanceof Error ? error.message : 'Unknown error'}`,
        errorCode: StorageErrorCode.TRANSACTION_FAILED
      };
    }
  }

  /**
   * Mark chart as deleted (soft delete)
   */
  async markChartAsDeleted(id: string, deletedAt: Date, permanentDeleteAt: Date): Promise<StorageResult<void>> {
    const notInit = this.checkInitialized<void>();
    if (notInit) return notInit;

    try {
      // Get existing metadata
      const metadata = await this.db.get('metadata', id);
      if (!metadata) {
        return {
          success: false,
          error: 'Chart not found',
          errorCode: StorageErrorCode.NOT_FOUND
        };
      }

      // Update metadata with soft delete fields
      const updatedMetadata: ChartMetadata = {
        ...metadata,
        isDeleted: true,
        deletedAt,
        permanentDeleteAt,
        modified: new Date()
      };

      // Save updated metadata
      await this.db.put('metadata', updatedMetadata);

      return { success: true };

    } catch (error) {
      devError('Failed to mark chart as deleted:', error);
      return {
        success: false,
        error: `Failed to mark chart as deleted: ${error instanceof Error ? error.message : 'Unknown error'}`,
        errorCode: StorageErrorCode.TRANSACTION_FAILED
      };
    }
  }

  /**
   * Undo chart deletion (restore from soft delete)
   */
  async undoDeleteChart(id: string): Promise<StorageResult<void>> {
    const notInit = this.checkInitialized<void>();
    if (notInit) return notInit;

    try {
      // Get existing metadata
      const metadata = await this.db.get('metadata', id);
      if (!metadata) {
        return {
          success: false,
          error: 'Chart not found',
          errorCode: StorageErrorCode.NOT_FOUND
        };
      }

      // Update metadata to remove soft delete fields
      const updatedMetadata: ChartMetadata = {
        ...metadata,
        isDeleted: false,
        deletedAt: undefined,
        permanentDeleteAt: undefined,
        modified: new Date()
      };

      // Save updated metadata
      await this.db.put('metadata', updatedMetadata);

      return { success: true };

    } catch (error) {
      devError('Failed to undo chart deletion:', error);
      return {
        success: false,
        error: `Failed to undo chart deletion: ${error instanceof Error ? error.message : 'Unknown error'}`,
        errorCode: StorageErrorCode.TRANSACTION_FAILED
      };
    }
  }

  /**
   * Clean up expired soft-deleted charts
   */
  private async cleanupExpiredDeletedCharts(metadata: ChartMetadata[]): Promise<void> {
    const now = new Date();
    const expiredCharts = metadata.filter(meta => 
      meta.isDeleted && 
      meta.permanentDeleteAt && 
      meta.permanentDeleteAt <= now
    );

    for (const chart of expiredCharts) {
      try {
        // Permanently delete expired chart
        await this.db.delete('charts', chart.id);
        await this.db.delete('metadata', chart.id);
        devLog(`Automatically deleted expired chart: ${chart.name} (${chart.id})`);
      } catch (error) {
        devError(`Failed to auto-delete expired chart ${chart.id}:`, error);
      }
    }
  }

  /**
   * Delete chart by ID (hard delete)
   */
  async deleteChart(id: string): Promise<StorageResult<void>> {
    const notInit = this.checkInitialized<void>();
    if (notInit) return notInit;

    try {
      // Check if chart exists
      const chart = await this.db.get('charts', id);
      if (!chart) {
        return {
          success: false,
          error: 'Chart not found',
          errorCode: StorageErrorCode.NOT_FOUND
        };
      }

      // Delete from both stores
      await this.db.delete('charts', id);
      await this.db.delete('metadata', id);

      
      return { success: true };

    } catch (error) {
      devError('Failed to delete chart:', error);
      return {
        success: false,
        error: `Failed to delete chart: ${error instanceof Error ? error.message : 'Unknown error'}`,
        errorCode: StorageErrorCode.TRANSACTION_FAILED
      };
    }
  }

  /**
   * List all charts with options
   */
  async listCharts(options: ListChartsOptions = {}): Promise<StorageResult<ChartMetadata[]>> {
    const notInit = this.checkInitialized<ChartMetadata[]>();
    if (notInit) return notInit;

    try {
      let metadata = await this.db.getAll('metadata');

      // Clean up expired soft-deleted items
      await this.cleanupExpiredDeletedCharts(metadata);
      
      // Refresh metadata after cleanup
      metadata = await this.db.getAll('metadata');

      // Filter by search term
      if (options.searchTerm) {
        const searchLower = options.searchTerm.toLowerCase();
        metadata = metadata.filter(meta => 
          meta.name.toLowerCase().includes(searchLower) ||
          (meta.description && meta.description.toLowerCase().includes(searchLower))
        );
      }

      // Filter by tags
      if (options.tags && options.tags.length > 0) {
        metadata = metadata.filter(meta => 
          meta.tags && meta.tags.some((tag: string) => options.tags!.includes(tag))
        );
      }

      // Sort
      const sortBy = options.sortBy || 'modified';
      const sortOrder = options.sortOrder || 'desc';
      
      metadata.sort((a, b) => {
        let aVal: any = a[sortBy as keyof ChartMetadata];
        let bVal: any = b[sortBy as keyof ChartMetadata];
        
        if (aVal instanceof Date) aVal = aVal.getTime();
        if (bVal instanceof Date) bVal = bVal.getTime();
        
        if (sortOrder === 'asc') {
          return aVal > bVal ? 1 : -1;
        } else {
          return aVal < bVal ? 1 : -1;
        }
      });

      // Pagination
      if (options.offset) {
        metadata = metadata.slice(options.offset);
      }
      if (options.limit) {
        metadata = metadata.slice(0, options.limit);
      }

      return { 
        success: true, 
        data: metadata 
      };

    } catch (error) {
      devError('Failed to list charts:', error);
      return {
        success: false,
        error: `Failed to list charts: ${error instanceof Error ? error.message : 'Unknown error'}`,
        errorCode: StorageErrorCode.TRANSACTION_FAILED
      };
    }
  }

  /**
   * Get recent charts
   */
  async getRecentCharts(limit?: number): Promise<StorageResult<ChartMetadata[]>> {
    const actualLimit = limit || this.config.maxRecentFiles;
    
    return await this.listCharts({
      sortBy: 'accessed',
      sortOrder: 'desc',
      limit: actualLimit
    });
  }

  /**
   * Get storage statistics
   */
  async getStorageStats(): Promise<StorageResult<{
    totalCharts: number;
    totalSize: number;
    usage?: number;
    quota?: number;
  }>> {
    const notInit = this.checkInitialized<{ totalCharts: number; totalSize: number; usage?: number; quota?: number }>();
    if (notInit) return notInit;

    try {
      const metadata = await this.db.getAll('metadata');
      const totalCharts = metadata.length;
      const totalSize = metadata.reduce((sum, meta) => sum + (meta.size || 0), 0);
      
      const estimate = await this.db.getStorageEstimate();
      
      return {
        success: true,
        data: {
          totalCharts,
          totalSize,
          usage: estimate?.usage,
          quota: estimate?.quota
        }
      };

    } catch (error) {
      devError('Failed to get storage stats:', error);
      return {
        success: false,
        error: `Failed to get storage stats: ${error instanceof Error ? error.message : 'Unknown error'}`,
        errorCode: StorageErrorCode.TRANSACTION_FAILED
      };
    }
  }

  /**
   * Clear all data
   */
  async clearAllData(): Promise<StorageResult<void>> {
    const notInit = this.checkInitialized<void>();
    if (notInit) return notInit;

    try {
      await this.db.clear('charts');
      await this.db.clear('metadata');
      
      
      return { success: true };

    } catch (error) {
      devError('Failed to clear data:', error);
      return {
        success: false,
        error: `Failed to clear data: ${error instanceof Error ? error.message : 'Unknown error'}`,
        errorCode: StorageErrorCode.TRANSACTION_FAILED
      };
    }
  }

  /**
   * Save default workspace state
   * Extension for multiple charts architecture
   */
  async saveDefaultWorkspace(workspace: any): Promise<StorageResult<void>> {
    if (!this.initialized) {
      return {
        success: false,
        error: 'Storage service not initialized',
        errorCode: StorageErrorCode.STORAGE_UNAVAILABLE
      };
    }

    try {
      await this.db.put('workspaces', { ...workspace, id: 'default' });
      
      
      return { success: true };

    } catch (error) {
      devError('Failed to save workspace:', error);
      return {
        success: false,
        error: `Failed to save workspace: ${error instanceof Error ? error.message : 'Unknown error'}`,
        errorCode: StorageErrorCode.TRANSACTION_FAILED
      };
    }
  }

  /**
   * Get default workspace state
   * Extension for multiple charts architecture
   */
  async getDefaultWorkspace(): Promise<StorageResult<any>> {
    if (!this.initialized) {
      return {
        success: false,
        error: 'Storage service not initialized',
        errorCode: StorageErrorCode.STORAGE_UNAVAILABLE
      };
    }

    try {
      const workspace = await this.db.get('workspaces', 'default');
      
      if (!workspace) {
        return {
          success: true,
          data: null
        };
      }
      
      return {
        success: true,
        data: workspace
      };

    } catch (error) {
      devError('Failed to get workspace:', error);
      return {
        success: false,
        error: `Failed to get workspace: ${error instanceof Error ? error.message : 'Unknown error'}`,
        errorCode: StorageErrorCode.TRANSACTION_FAILED
      };
    }
  }

  /**
   * Clear workspace data
   * Extension for multiple charts architecture
   */
  async clearWorkspaces(): Promise<StorageResult<void>> {
    if (!this.initialized) {
      return {
        success: false,
        error: 'Storage service not initialized',
        errorCode: StorageErrorCode.STORAGE_UNAVAILABLE
      };
    }

    try {
      await this.db.clear('workspaces');
      
      
      return { success: true };

    } catch (error) {
      devError('Failed to clear workspaces:', error);
      return {
        success: false,
        error: `Failed to clear workspaces: ${error instanceof Error ? error.message : 'Unknown error'}`,
        errorCode: StorageErrorCode.TRANSACTION_FAILED
      };
    }
  }

  /**
   * Update chart metadata (for favorites, access time, etc.)
   * Extension for multiple charts architecture
   */
  async updateChartMetadata(id: string, updates: Partial<ChartMetadata>): Promise<StorageResult<void>> {
    if (!this.initialized) {
      return {
        success: false,
        error: 'Storage service not initialized',
        errorCode: StorageErrorCode.STORAGE_UNAVAILABLE
      };
    }

    try {
      const existingMetadata = await this.db.get('metadata', id);

      if (!existingMetadata) {
        return {
          success: false,
          error: 'Chart not found',
          errorCode: StorageErrorCode.NOT_FOUND
        };
      }

      // Deep merge chartDateTime object if it exists in updates
      const updatedMetadata = {
        ...existingMetadata,
        ...updates,
        // Properly merge chartDateTime instead of replacing it
        ...(updates.chartDateTime ? {
          chartDateTime: {
            ...existingMetadata.chartDateTime,
            ...updates.chartDateTime
          }
        } : {}),
        modified: new Date()
      };


      await this.db.put('metadata', updatedMetadata);

      // Also update the metadata copy in the charts store for consistency
      const existingChart = await this.db.get('charts', id);
      if (existingChart) {
        const updatedChart = {
          ...existingChart,
          metadata: updatedMetadata
        };
        await this.db.put('charts', updatedChart);
      }

      return { success: true };

    } catch (error) {
      devError('Failed to update chart metadata:', error);
      return {
        success: false,
        error: `Failed to update chart metadata: ${error instanceof Error ? error.message : 'Unknown error'}`,
        errorCode: StorageErrorCode.TRANSACTION_FAILED
      };
    }
  }

  /**
   * Update existing chart data (not just metadata)
   */
  async updateChartData(id: string, chartData: ParsedChartData): Promise<StorageResult<void>> {
    if (!this.initialized) {
      return {
        success: false,
        error: 'Storage service not initialized',
        errorCode: StorageErrorCode.STORAGE_UNAVAILABLE
      };
    }

    try {
      // Get existing chart
      const existingChart = await this.db.get('charts', id);
      
      if (!existingChart) {
        return {
          success: false,
          error: 'Chart not found',
          errorCode: StorageErrorCode.NOT_FOUND
        };
      }

      // Update chart data while preserving metadata
      const updatedChart = {
        ...existingChart,
        chartData,
        metadata: {
          ...existingChart.metadata,
          modified: new Date(),
          size: this.calculateSize(chartData)
        }
      };

      // Save updated chart and metadata
      await this.db.put('charts', updatedChart);
      await this.db.put('metadata', updatedChart.metadata);
      
      return { success: true };

    } catch (error) {
      devError('Failed to update chart data:', error);
      return {
        success: false,
        error: `Failed to update chart data: ${error instanceof Error ? error.message : 'Unknown error'}`,
        errorCode: StorageErrorCode.TRANSACTION_FAILED
      };
    }
  }

  /**
   * Update only apiResponse in chart data (for metadata-only changes like name/place)
   * This is optimized to avoid triggering full chart re-renders
   */
  async updateChartApiResponse(id: string, apiResponse: any): Promise<StorageResult<void>> {
    if (!this.initialized) {
      return {
        success: false,
        error: 'Storage service not initialized',
        errorCode: StorageErrorCode.STORAGE_UNAVAILABLE
      };
    }

    try {
      // Get existing chart
      const existingChart = await this.db.get('charts', id);

      if (!existingChart) {
        return {
          success: false,
          error: 'Chart not found',
          errorCode: StorageErrorCode.NOT_FOUND
        };
      }

      // Update apiResponse in chartData while preserving everything else
      const updatedChart = {
        ...existingChart,
        chartData: {
          ...existingChart.chartData,
          apiResponse
        },
        metadata: {
          ...existingChart.metadata,
          modified: new Date()
        }
      };

      // Save updated chart
      await this.db.put('charts', updatedChart);
      await this.db.put('metadata', updatedChart.metadata);

      return { success: true };

    } catch (error) {
      devError('Failed to update chart apiResponse:', error);
      return {
        success: false,
        error: `Failed to update chart apiResponse: ${error instanceof Error ? error.message : 'Unknown error'}`,
        errorCode: StorageErrorCode.TRANSACTION_FAILED
      };
    }
  }

  /**
   * Close storage service
   */
  close(): void {
    this.db.close();
    this.initialized = false;
  }
}

// Singleton instance
let storageServiceInstance: StorageService | null = null;

/**
 * Get singleton storage service instance
 */
export function getStorageService(): StorageService {
  if (!storageServiceInstance) {
    storageServiceInstance = new StorageService();
  }
  return storageServiceInstance;
}

/**
 * Initialize storage service (call once at app startup)
 */
export async function initializeStorage(): Promise<StorageResult<void>> {
  const service = getStorageService();
  return await service.initialize();
}
